import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, ArrowRightLeft, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";

interface ClientReferral {
  id: number;
  person_id: number;
  agency_id: number;
  referralDate: string | null;
  referralSourceId: number | null;
  referralSourceName: string | null;
  status: string | null;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  author_first_name?: string;
  author_last_name?: string;
}

interface ReferralSource {
  id: number;
  name: string;
  category: string | null;
}

interface ReferralsListProps {
  personId: string;
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

export function ReferralsList({ personId }: ReferralsListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingReferral, setEditingReferral] = useState<ClientReferral | null>(null);

  const { data, isLoading } = useQuery<{ data: ClientReferral[] }>({
    queryKey: ["clientReferrals", personId],
    queryFn: () => api.get<{ data: ClientReferral[] }>(`/clients/${personId}/referrals`),
    enabled: !!personId,
  });

  const { data: sourcesData } = useQuery<{ data: ReferralSource[] }>({
    queryKey: ["referralSources"],
    queryFn: () => api.get<{ data: ReferralSource[] }>("/admin/referral-sources"),
  });

  const createMutation = useMutation({
    mutationFn: (body: { referralSourceId?: number; referralSourceName?: string; referralDate?: string; status?: string; notes?: string }) =>
      api.post(`/clients/${personId}/referrals`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientReferrals", personId] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number; referralSourceId?: number; referralSourceName?: string; referralDate?: string; status?: string; notes?: string }) =>
      api.put(`/clients/${personId}/referrals/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientReferrals", personId] });
      setEditingReferral(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/clients/${personId}/referrals/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["clientReferrals", personId] }),
  });

  const referrals = data?.data ?? [];
  const sources = sourcesData?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Referrals</h3>
        <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          Add Referral
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <ReferralForm
              sources={sources}
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {editingReferral && (
        <Card>
          <CardContent className="p-4">
            <ReferralForm
              sources={sources}
              initial={editingReferral}
              onSubmit={(d) => updateMutation.mutate({ id: editingReferral.id, ...d })}
              onCancel={() => setEditingReferral(null)}
              isLoading={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && <div className="h-20 animate-pulse rounded-lg border bg-muted" />}

      {!isLoading && referrals.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <ArrowRightLeft className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No referrals recorded.</p>
          </CardContent>
        </Card>
      )}

      {referrals.map((r) => {
        const authorName = r.author_first_name && r.author_last_name
          ? `${r.author_first_name} ${r.author_last_name}` : null;

        return (
          <Card key={r.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-medium">
                      {r.referralSourceName ?? "Unknown Source"}
                    </CardTitle>
                    {r.status && (
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusColors[r.status] ?? "bg-gray-100 text-gray-800"}`}>
                        {r.status.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                  <CardDescription className="mt-0.5">
                    {r.referralDate ? new Date(r.referralDate).toLocaleDateString() : "No date"}
                    {authorName && ` | By: ${authorName}`}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => setEditingReferral(r)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => {
                    if (confirm("Delete this referral?")) deleteMutation.mutate(r.id);
                  }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            {r.notes && (
              <CardContent>
                <p className="text-xs text-muted-foreground">{r.notes}</p>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

interface ReferralFormProps {
  sources: ReferralSource[];
  initial?: ClientReferral;
  onSubmit: (data: { referralSourceId?: number; referralSourceName?: string; referralDate?: string; status?: string; notes?: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function ReferralForm({ sources, initial, onSubmit, onCancel, isLoading }: ReferralFormProps) {
  const [sourceId, setSourceId] = useState(initial?.referralSourceId ? String(initial.referralSourceId) : "");
  const [sourceName, setSourceName] = useState(initial?.referralSourceName ?? "");
  const [referralDate, setReferralDate] = useState(initial?.referralDate ?? "");
  const [status, setStatus] = useState(initial?.status ?? "pending");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Referral Source</label>
          <select
            value={sourceId}
            onChange={(e) => {
              setSourceId(e.target.value);
              const s = sources.find((x) => x.id === parseInt(e.target.value, 10));
              if (s) setSourceName(s.name);
            }}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select source...</option>
            {sources.map((s) => <option key={s.id} value={s.id}>{s.name}{s.category ? ` (${s.category})` : ""}</option>)}
          </select>
        </div>
        <Input label="Or Enter Name" value={sourceName} onChange={(e) => setSourceName(e.target.value)} placeholder="Custom source name..." />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Referral Date" type="date" value={referralDate} onChange={(e) => setReferralDate(e.target.value)} />
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="declined">Declined</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional details..." />
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() => onSubmit({
            referralSourceId: sourceId ? parseInt(sourceId, 10) : undefined,
            referralSourceName: sourceName || undefined,
            referralDate: referralDate || undefined,
            status: status || undefined,
            notes: notes || undefined,
          })}
          loading={isLoading}
        >
          {initial ? "Update Referral" : "Add Referral"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
